import authService from './auth.service';
import { SkillApiError } from './skill.service';
import type {
  AvailableSkillsResponse,
  GrantSkillAccessResponse,
  RevokeSkillAccessResponse,
  RoleSkillAccessResponse,
  SetRoleSkillsResponse,
  SkillAccessHistoryResponse,
} from '../types/skill-access.types';

const BASE = '/api/v1/agent/skill-access';
const SKILLS_BASE = '/api/v1/agent/skills';

async function handleResponse<T>(res: Response, fallback: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  const err = await res.json().catch(() => null);
  const detail = typeof err?.detail === 'string' ? err.detail : fallback;
  throw new SkillApiError(res.status, detail);
}

class SkillAccessService {
  async getAvailable(): Promise<AvailableSkillsResponse> {
    const res = await authService.fetchWithAuth(`${SKILLS_BASE}/available`);
    return handleResponse<AvailableSkillsResponse>(res, 'Не удалось загрузить доступные скиллы');
  }

  async listForRole(roleId: number): Promise<RoleSkillAccessResponse> {
    const res = await authService.fetchWithAuth(`${BASE}/roles/${roleId}`);
    return handleResponse<RoleSkillAccessResponse>(res, 'Не удалось загрузить доступы роли');
  }

  async grant(roleId: number, skillCode: string): Promise<GrantSkillAccessResponse> {
    const res = await authService.fetchWithAuth(`${BASE}/roles/${roleId}/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skill_code: skillCode }),
    });
    return handleResponse<GrantSkillAccessResponse>(res, 'Не удалось выдать доступ');
  }

  async setBulk(roleId: number, skillCodes: string[]): Promise<SetRoleSkillsResponse> {
    const res = await authService.fetchWithAuth(`${BASE}/roles/${roleId}/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skill_codes: skillCodes }),
    });
    return handleResponse<SetRoleSkillsResponse>(res, 'Не удалось сохранить доступы');
  }

  async revoke(roleId: number, skillCode: string): Promise<RevokeSkillAccessResponse> {
    const res = await authService.fetchWithAuth(
      `${BASE}/roles/${roleId}/${encodeURIComponent(skillCode)}`,
      { method: 'DELETE' },
    );
    return handleResponse<RevokeSkillAccessResponse>(res, 'Не удалось отозвать доступ');
  }

  async history(
    roleId: number,
    params: { limit?: number; offset?: number } = {},
  ): Promise<SkillAccessHistoryResponse> {
    const qs = new URLSearchParams({
      limit: String(params.limit ?? 50),
      offset: String(params.offset ?? 0),
    });
    const res = await authService.fetchWithAuth(`${BASE}/roles/${roleId}/history?${qs}`);
    return handleResponse<SkillAccessHistoryResponse>(res, 'Не удалось загрузить историю');
  }
}

const skillAccessService = new SkillAccessService();
export default skillAccessService;
